import { useCallback, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { ProductSelection } from '@nucleus-commerce/api-client/admin';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { selectionChanged, type SelectionChange } from './selection-errors';

export interface SelectionRequest {
  /**
   * Sends `send(selection)`. When the server answers 409 SELECTION_CHANGED (the filter matches
   * another number of products than the user saw), asks whether to apply it to the new number
   * and, on yes, sends again with `expectedCount` set to it. Resolves `null` when the user says
   * no; every other error rejects as usual.
   */
  run: <T>(
    selection: ProductSelection,
    send: (selection: ProductSelection) => Promise<T>,
  ) => Promise<T | null>;
  /** The confirm dialog; render it once next to the action. */
  dialog: ReactNode;
}

interface Question {
  change: SelectionChange;
  answer: (apply: boolean) => void;
}

/** The "filter now matches 1,250 products (was 1,243)" confirm around a selection request. */
export function useSelectionRequest(): SelectionRequest {
  const { t, i18n } = useTranslation('common');
  const [question, setQuestion] = useState<Question | null>(null);

  const run = useCallback(
    async <T,>(
      selection: ProductSelection,
      send: (selection: ProductSelection) => Promise<T>,
    ): Promise<T | null> => {
      let current = selection;
      for (;;) {
        try {
          return await send(current);
        } catch (error) {
          const change = selectionChanged(error);
          if (!change || current.mode !== 'query') throw error;
          const apply = await new Promise<boolean>((resolve) =>
            setQuestion({ change, answer: resolve }),
          );
          setQuestion(null);
          if (!apply) return null;
          current = { ...current, expectedCount: change.actual };
        }
      }
    },
    [],
  );

  const number = new Intl.NumberFormat(i18n.language);
  const counts = question
    ? {
        count: question.change.actual,
        actual: number.format(question.change.actual),
        expected: number.format(question.change.expected),
      }
    : undefined;

  const dialog = (
    <AlertDialog
      open={question !== null}
      onOpenChange={(open) => {
        if (!open) question?.answer(false);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('common:bulkSelection.changedTitle')}</AlertDialogTitle>
          <AlertDialogDescription>
            {counts ? t('common:bulkSelection.changed', counts) : null}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
          <AlertDialogAction onClick={() => question?.answer(true)}>
            {counts ? t('common:bulkSelection.apply', counts) : null}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { run, dialog };
}
